import React, { forwardRef, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { ArrowRightIcon, Caption1, Caption2, Header2 } from "@activecollab/components";
import classNames from "classnames";
import moment from "moment";
import PropTypes from "prop-types";
import { getPersonalizedTimeFormat } from "~/store/selectors/selector.configOptions";

const TriggerDateContainer = ({ date, time, disabled }) => {
  const formatTime = useSelector(getPersonalizedTimeFormat);
  const dateTime = moment.unix(date);

  const isCurrentYear = useMemo(() => {
    return moment.unix(date).isSame(moment(), "year");
  }, [date]);

  return (
    <div
      className={classNames(
        "tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-lg tw-h-[62px] tw-w-[80px] tw-bg-[var(--input-background-color)] tw-border tw-border-[var(--color-theme-500)] tw-border-solid",
        {
          "tw-opacity-50": disabled,
        }
      )}
    >
      <Caption1
        weight="medium"
        color="theme-primary"
      >
        {dateTime.format("MMM")}
      </Caption1>

      <Header2
        weight="bold"
        color="theme-primary"
      >
        {dateTime.date()}
      </Header2>
      {time ? (
        <Caption2
          weight="medium"
          color="secondary"
        >
          {dateTime.format(formatTime)}
        </Caption2>
      ) : !isCurrentYear ? (
        <Caption2
          weight="medium"
          color="secondary"
        >
          {dateTime.format("YYYY")}
        </Caption2>
      ) : (
        <Caption2
          weight="medium"
          color="secondary"
        >
          {dateTime.format("ddd")}
        </Caption2>
      )}
    </div>
  );
};

TriggerDateContainer.propTypes = {
  date: PropTypes.number,
  time: PropTypes.string,
  disabled: PropTypes.bool,
};

export const CalendarEventDateTrigger = forwardRef(
  ({ startsOn, startsOnTime, endsOn, endsOnTime, onClick, disabled }, ref) => {
    const handleClick = useCallback(() => {
      if (disabled) {
        return;
      }
      if (typeof onClick === "function") {
        onClick();
      }
    }, [disabled, onClick]);

    return (
      <div
        data-testid="calendar-event-date-trigger"
        ref={ref}
        onClick={handleClick}
        className={classNames("tw-flex tw-flex-1 tw-items-center tw-flex-row tw-gap-4 tw-justify-center tw-max-w-fit", {
          "tw-cursor-pointer": !disabled,
        })}
        aria-disabled={disabled}
      >
        <TriggerDateContainer
          date={startsOn}
          time={startsOnTime}
          disabled={disabled}
        />
        {startsOn !== endsOn ? (
          <>
            <ArrowRightIcon />
            <TriggerDateContainer
              date={endsOn}
              time={endsOnTime}
              disabled={disabled}
            />
          </>
        ) : null}
      </div>
    );
  }
);

CalendarEventDateTrigger.displayName = "CalendarEventDateTrigger";
CalendarEventDateTrigger.propTypes = {
  startsOn: PropTypes.number,
  startsOnTime: PropTypes.string,
  endsOn: PropTypes.number,
  endsOnTime: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
};
